import {color, init_tooltips, number_format, on} from './util';
import * as Highcharts from "highcharts";
import DataTable, {Order} from "datatables.net-bs5";
import { groupColor } from './constants';
import submit from "./home/submit";
import { initModal, openModal } from './home/modal';
import { initRunsModal, openRunsModal } from './home/runs-modal';
import { openStairmasterModal } from './home/stairmaster-modal';
import { initGymModal, openGymModal } from './home/gym-modal';
import { initSortable } from './home/sortable';
import { makeEmptyAwareTooltip, wireNullAwareHover } from './utils/emptyAwareTooltip';
import { initImportModal } from './import-modal';
import { getChartTheme } from './utils/chartTheme';
declare const data_array : any[];
declare const dates_array : string[];
declare const source_data : any[];
declare const source_language_data : any[];
declare const heatmap_data : any;

export default class {
    static init = () => {
        init();
        charts();
        tables();
        source();
        source_language();
        submit.init();
        months();
        heatmaps();
        initModal();
        initRunsModal();
        initGymModal();
        initSortable();
        initImportModal({
            onSuccess: (_result) => {
                // index.ts runs home.init() on EVERY page; only the Home host owns the
                // in-place transactions-chart redraw. Guard on the home chart's presence so
                // this never fires the relative ?do=getTransactionsSeries on a non-Home
                // presenter (e.g. on /transactions → TransactionsPresenter → 403). On the home
                // page the relative signal correctly resolves to HomePresenter.
                if (!findChartById('chart_transactions')) return;
                // Phase 183 (UX-02, D-08/D-10): show skeleton shimmer around the post-import
                // chart-reload fetch, hide once the redraw completes. Toggle the OUTER container
                // by id (#home-chart-skeleton), never the bare .ksat-skeleton-group class — the
                // shared ask-modal renders a second copy of that class on every show_header page
                // (WR-02/WR-03).
                const chartSkel = document.getElementById('home-chart-skeleton');
                if (chartSkel) chartSkel.hidden = false;
                fetch('?do=getTransactionsSeries', { method: 'POST' })
                    .then(r => r.json())
                    .then((data: any) => {
                        if (data.success) redrawTransactionsChart(data.series);
                    })
                    .catch(() => { /* silent — chart stays stale on network error */ })
                    .finally(() => { if (chartSkel) chartSkel.hidden = true; });
            }
        });
    }
}

// ── Highcharts helpers for post-import series redraw ────────────────────────

function findChartById(chartId: string): Highcharts.Chart | undefined {
    //@ts-ignore
    return (Highcharts.charts as any[]).find((c: any) => c?.renderTo?.id === chartId);
}

function redrawTransactionsChart(seriesData: { name: string[]; data: any[][] }): void {
    const chart = findChartById('chart_transactions');
    if (!chart) return;

    // Remove ALL existing series (series count may change after import — Pitfall 6)
    while (chart.series.length > 0) {
        chart.series[0].remove(false);
    }

    // Add new series — never pass undefined options (Highcharts landmine)
    seriesData.name.forEach((name: string, i: number) => {
        const color = groupColor(name);
        chart.addSeries({
            type: 'column',
            name,
            data: seriesData.data[i],
            ...(color !== undefined ? { color } : {}),
            connectNulls: true,
        } as Highcharts.SeriesOptionsType, false);
    });

    chart.redraw();
}

function init() {
    on('change', 'select[name="year"]', (element : HTMLInputElement) => {
        location.href = '?year=' + element.value;
    });
    on('change','input[name="from"],input[name="to"]',(element : HTMLInputElement) => {
        let from = element.name === 'from' ? element.value : document.querySelector<HTMLInputElement>('input[name="from"]')?.value;
        let to = element.name === 'to' ? element.value : document.querySelector<HTMLInputElement>('input[name="to"]')?.value;
        location.href = '?from=' + from + '&to=' + to;
    });
    on('change', '.js-yoy-toggle', (element: HTMLInputElement) => {
        location.href = '?yoy=' + ((element as HTMLInputElement).checked ? '1' : '0');
    });
}

const ALC_COLOR_ESTIMATE: Highcharts.GradientColorObject = {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
        [0, '#E1A818'],
        [0.1, 'rgba(59, 171, 211, 0.45)'],
        [0.9, 'rgba(59, 171, 211, 0.45)'],
        [1, '#E1A818'],
    ],
};

function applyEstimateStyle(
    points: Highcharts.PointOptionsObject[],
): Highcharts.PointOptionsObject[] {
    return points.map(pt => {
        if (typeof pt !== 'object' || pt === null) return pt;
        const isEstimate = (pt as any).custom?.estimate === 1;
        if (!isEstimate) return pt;
        return {
            ...pt,
            color: ALC_COLOR_ESTIMATE,
            borderWidth: 1,
            dashStyle: 'ShortDash' as Highcharts.DashStyleValue,
        };
    });
}

function applyOuraStyle(
    points: Highcharts.PointOptionsObject[],
): Highcharts.PointOptionsObject[] {
    return points.map(pt => {
        if (typeof pt !== 'object' || pt === null) return pt;
        if ((pt as any).hasOura !== true) return pt;
        return {
            ...pt,
            marker: {
                enabled: true,
                symbol: 'circle',
                radius: 5,
                fillColor: 'var(--ksat-accent)',
                lineColor: 'var(--ksat-accent)',
                lineWidth: 2,
            },
        };
    });
}

/** Extract the y-value from a raw prior_data entry (object with .y, or raw number/null). */
function extractPriorY(raw: any): number | null {
    if (raw == null) return null;
    if (typeof raw === 'object') return (raw as any).y ?? null;
    return raw as number | null;
}

/**
 * Linearly interpolate a value at ptIdx from the nearest non-null neighbours.
 * Works with both flat number|null arrays (current-year) and object-with-y arrays (prior alc).
 * Returns null when neighbours cannot be found on both sides (gap at edge or all-null).
 */
function interpolateAt(arr: any[], ptIdx: number): number | null {
    let loIdx = -1, hiIdx = -1;
    for (let i = ptIdx - 1; i >= 0; i--) {
        const v = extractPriorY(arr[i]);
        if (v !== null) { loIdx = i; break; }
    }
    for (let i = ptIdx + 1; i < arr.length; i++) {
        const v = extractPriorY(arr[i]);
        if (v !== null) { hiIdx = i; break; }
    }
    if (loIdx === -1 || hiIdx === -1) return null;
    const lo = extractPriorY(arr[loIdx]) as number;
    const hi = extractPriorY(arr[hiIdx]) as number;
    const t = (ptIdx - loIdx) / (hiIdx - loIdx);
    return lo + (hi - lo) * t;
}

function buildYoyExtraRows(
    priorDataArr: any[] | undefined,
    chartId: string,
    currentDataArr?: any[],
    suffixFmt?: (y: number) => string,
): (ctx: Highcharts.Point) => string[] {
    if (!priorDataArr) return () => [];
    const isLineSeries = chartId === 'weight' || chartId === 'waist';
    return (ctx) => {
        const ptIdx = ((ctx as any).point as any)?.index as number | undefined;
        if (ptIdx == null) return [];

        // --- Current value ---
        let currentY = ctx.y as number | null;
        let isCurrentEstimate = false;
        if (currentY === null && isLineSeries && currentDataArr) {
            const interpolated = interpolateAt(currentDataArr, ptIdx);
            if (interpolated !== null) {
                currentY = parseFloat(interpolated.toFixed(1));
                isCurrentEstimate = true;
            }
        }

        // --- Prior value ---
        const priorRaw = priorDataArr[ptIdx];
        let priorY: number | null = extractPriorY(priorRaw);
        let isPriorEstimate = false;
        if (priorY === null && isLineSeries) {
            const interpolated = interpolateAt(priorDataArr, ptIdx);
            if (interpolated !== null) {
                priorY = parseFloat(interpolated.toFixed(1));
                isPriorEstimate = true;
            }
        }

        // alc is stored negative for inverted display — compute on magnitudes
        const cur = chartId === 'alc' && currentY != null ? Math.abs(currentY) : currentY;
        const pri = chartId === 'alc' && priorY   != null ? Math.abs(priorY)   : priorY;

        const estMark = ' <span style="color:#999;font-style:italic;">(est.)</span>';
        const curFmt  = cur != null
            ? `${suffixFmt ? suffixFmt(cur) : cur}${isCurrentEstimate ? estMark : ''}`
            : '—';
        const priFmt  = pri != null
            ? `${suffixFmt ? suffixFmt(pri) : pri}${isPriorEstimate  ? estMark : ''}`
            : '—';

        let chgFmt = '—';
        if (cur != null && pri != null && pri !== 0) {
            const pct = ((cur - pri) / pri) * 100;
            const sign = pct >= 0 ? '+' : '';
            const chgEst = (isCurrentEstimate || isPriorEstimate) ? estMark : '';
            chgFmt = `${sign}${Math.round(pct)}%${chgEst}`;
        }
        return [
            `<span style="color:#999">Current:</span> <b>${curFmt}</b><br/>`,
            `<span style="color:#999">Prior:</span> <b>${priFmt}</b><br/>`,
            `<span style="color:#999">Change:</span> <b>${chgFmt}</b><br/>`,
        ];
    };
}

const LBS_TO_KG = 0.45359237;
const CM_TO_IN  = 1 / 2.54;

function fmtDualUnit(primaryVal: number, suffix: string, chartId: string): string {
    if (chartId === 'weight') {
        return `${primaryVal}${suffix} (${(primaryVal * LBS_TO_KG).toFixed(1)} kg)`;
    }
    if (chartId === 'waist') {
        return `${primaryVal}${suffix} (${(primaryVal * CM_TO_IN).toFixed(1)} in)`;
    }
    return `${primaryVal}${suffix}`;
}

function charts() {
    if(typeof data_array !== 'undefined') {
        // Guard flag: prevents chart.events.click from firing when a data point click
        // already handled the event. Highcharts point/chart events are independent
        // callbacks (not DOM events), so e.stopPropagation() does NOT work here.
        let _pointClickInProgress = false;

        const button_background = getComputedStyle(document.documentElement).getPropertyValue('--ksat-surface-raised').trim();

        for(let [id, data] of Object.entries(data_array)) {
            if(!data.show) {
                continue;
            }
            let series = [] as Highcharts.SeriesOptionsType[];
            data.data.forEach((value : any, index : number) => {
                let seriesColor: any;
                if (id === 'transactions') {
                    seriesColor = groupColor(data.name[index]);
                } else if (id === 'alc') {
                    seriesColor = {
                        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                        stops: [
                            [0, '#E1A818'],
                            [0.1, data.colors[0]],
                            [0.9, data.colors[0]],
                            [1, '#E1A818']
                        ]
                    } as Highcharts.GradientColorObject;
                } else {
                    seriesColor = data.colors[index];
                }
                // trades: Gold (idx 2) and snap: Score (idx 1) render as a spline on the 2nd yAxis
                const isSecondaryAxisSpline = (id === 'trades' && index === 2) || (id === 'snap' && index === 1);
                series.push({
                    type: isSecondaryAxisSpline ? 'spline' : data.type,
                    name: data.name[index],
                    data: value,
                    yAxis: isSecondaryAxisSpline ? 1 : 0,
                    connectNulls: id === 'oura_steps' ? false : true,
                    color: seriesColor
                } as Highcharts.SeriesOptionsType);
            });
            // Add time overlay spline on right yAxis for km and stairmaster charts
            if ((id === 'km' || id === 'stairmaster') && data.time_data && data.time_data.length > 0) {
                series.push({
                    type: 'spline',
                    name: 'time',
                    data: data.time_data,
                    yAxis: 1,
                    connectNulls: false,
                    color: id === 'km' ? 'rgba(0,144,255,0.5)' : 'rgba(255,140,0,0.5)',
                    dashStyle: 'ShortDash',
                    marker: { enabled: false },
                    enableMouseTracking: false,
                } as Highcharts.SeriesOptionsType);
            }
            if (id === 'alc') {
                const alcSeries = series[0] as Highcharts.SeriesColumnOptions;
                alcSeries.data = applyEstimateStyle(
                    alcSeries.data as Highcharts.PointOptionsObject[]
                );
            }
            if (id === 'km') {
                const kmSeries = series[0] as Highcharts.SeriesColumnOptions;
                kmSeries.data = applyOuraStyle(
                    kmSeries.data as Highcharts.PointOptionsObject[]
                );
            }
            const hasYoy = (data_array as any).yoy_enabled === true;
            if (hasYoy && data.prior_data && ['alc', 'weight', 'waist'].includes(id.toString())) {
                const priorYear = data.prior_year ?? (new Date().getFullYear() - 1);
                const priorColor = data.colors[0]; // same hue; opacity dims it
                series.push({
                    type: data.type,
                    name: String(priorYear),
                    data: data.prior_data,
                    opacity: 0.4,
                    color: priorColor,
                    connectNulls: id === 'weight' || id === 'waist',
                    ...(id === 'weight' || id === 'waist'
                        ? { dashStyle: 'Dot' as Highcharts.DashStyleValue, marker: { enabled: false } }
                        : {}),
                } as Highcharts.SeriesOptionsType);
            }
            let yaxis = [{
                type: id == 'reading' ? 'datetime' : 'linear',
                title: {
                    text: '',
                },
                labels: {
                    formatter: id == 'reading' ? function () {
                        //@ts-ignore
                        return diffForHumans(this.value, 2);
                    } : undefined
                },
                minorGridLineWidth: 0.1,
                gridLineWidth: 0.1,
                min: data.min,
                max: data.max
            }] as Highcharts.YAxisOptions[];
            if(id === 'trades') {
                yaxis.push({
                    type: 'linear',
                    title: {
                        text: '',
                    },
                    minorGridLineWidth: 0.1,
                    gridLineWidth: 0.1,
                    min: data.min,
                    max: data.max,
                    //opposite: true
                });
            }
            if (id === 'snap') {
                // Cumulative-score axis (large values) on the right; bars (daily gain) use the left axis
                yaxis.push({
                    type: 'linear',
                    title: { text: '' },
                    minorGridLineWidth: 0,
                    gridLineWidth: 0,
                    opposite: true,
                });
            }
            if (id === 'km' || id === 'stairmaster') {
                yaxis.push({
                    type: 'linear',
                    title: {
                        text: '',
                    },
                    labels: {
                        formatter: function(this: any) {
                            return diffForHumans(this.value * 1000, 1);
                        }
                    },
                    minorGridLineWidth: 0,
                    gridLineWidth: 0,
                    opposite: true,
                });
            }
            const yoyExtraRows = buildYoyExtraRows(
                (data_array as any).yoy_enabled === true ? data.prior_data : undefined,
                id.toString(),
                (data_array as any).yoy_enabled === true ? data.data[0] : undefined,
                (id === 'weight' || id === 'waist')
                    ? (y: number) => fmtDualUnit(y, (data.suffix ?? '') as string, id.toString())
                    : undefined,
            );
            const hcChart = Highcharts.chart("chart_" + id.toString(), Highcharts.merge(getChartTheme(), {
                series: series,
                chart: {
                    // Phase 178 (D-07): transparent so the elevated .chart-wrapper surface shows through.
                    backgroundColor: 'transparent',
                    height: 320,
                    zooming: {
                        type: 'x',
                        resetButton: {
                            position: {
                                align: "right",
                                x: 0,
                                y: -30
                            },
                            theme: {
                                fill: button_background,
                                stroke: "none",
                                style: {
                                    backgroundColor: button_background
                                },
                                states: {
                                    hover: {
                                        fill: button_background,
                                        style: {
                                            backgroundColor: button_background
                                        }
                                    }
                                }
                            }
                        }
                    },
                    events: {
                        click: function (e) {
                            // Skip if a point click already handled this event
                            if (_pointClickInProgress) return;
                            let date = Highcharts.dateFormat('%Y-%m-%d', this.xAxis[0].toValue(e.chartX));
                            updateValue(id.toString(), date);
                        },
                        load: function () {
                            // Manually drive tooltip on null x-positions for empty-aware charts —
                            // Highcharts excludes null points from the kdTree so native hover skips them.
                            if (['alc', 'km', 'stairmaster', 'reading', 'weight', 'waist', 'gym', 'transactions', 'oura_steps'].includes(id.toString())) {
                                wireNullAwareHover(this);
                            }
                        },
                        render: function () {
                            // D-04: re-apply null-aware hover patch after any chart.update()
                            // (including responsive breakpoint crossing at 540px). Idempotency
                            // guard in wireNullAwareHover (_nullAwarePatched sentinel) prevents
                            // double-wrapping on repeated render events.
                            if (['alc', 'km', 'stairmaster', 'reading', 'weight', 'waist', 'gym', 'transactions', 'oura_steps'].includes(id.toString())) {
                                wireNullAwareHover(this);
                            }
                        },
                    }
                },
                title: {
                    text: data.title,
                    // align: 'left',
                    style: {
                        fontSize: '14px'
                    }
                },
                legend: {
                    enabled: (series.length > 1 && id !== 'km' && id !== 'stairmaster') || id === 'transactions',
                    ...(id === 'transactions' ? {
                        layout: 'horizontal' as const,
                        align: 'center' as const,
                        verticalAlign: 'bottom' as const,
                    } : {}),
                },
                xAxis: {
                    type: 'datetime',
                    labels: {
                        overflow: 'justify',
                    },
                    plotBands: generateWeekendPlotBands(dates_array),
                    crosshair: {
                        dashStyle: 'Dash',
                        color: getComputedStyle(document.documentElement).getPropertyValue('--ksat-border').trim(),
                        width: 1,
                        snap: true,
                        zIndex: 3,
                    },
                },
                // @ts-ignore
                yAxis: yaxis,
                tooltip: {
                    valueSuffix: data.suffix !== undefined ? data.suffix : '',
                    xDateFormat: '%b %e, %Y',
                    shared: ['transactions', 'alc', 'km', 'stairmaster', 'reading', 'weight', 'waist', 'gym', 'snap'].includes(id.toString()),
                    formatter: id == 'reading'
                        ? makeEmptyAwareTooltip({
                            formatValue: (ctx) => diffForHumans(ctx.y as number, 2),
                        })
                        : id === 'km'
                        ? makeEmptyAwareTooltip({
                            formatValue: (ctx) => {
                                const breakdown = ((ctx as any).point as any).breakdown;
                                const hasOura = ((ctx as any).point as any).hasOura === true;
                                const ouraLabel = hasOura
                                    ? ' <span style="color:var(--ksat-accent)">&#9899; Oura</span>'
                                    : '';
                                return `${ctx.y} km${breakdown ? ` (${breakdown})` : ''}${ouraLabel}`;
                            },
                        })
                        : id === 'stairmaster'
                        ? makeEmptyAwareTooltip({
                            formatValue: (ctx) => {
                                const breakdown = ((ctx as any).point as any).breakdown;
                                return `${ctx.y} steps${breakdown ? ` (${breakdown})` : ''}`;
                            },
                        })
                        : id === 'alc'
                        ? makeEmptyAwareTooltip({
                            formatValue: (ctx) => {
                                const value = Math.abs(ctx.y as number);
                                const isEstimate = ((ctx as any).point as any).custom?.estimate === 1;
                                const estLabel = isEstimate
                                    ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                                    : '';
                                return `${value}${estLabel}`;
                            },
                            extraRows: yoyExtraRows,
                        })
                        : id === 'transactions'
                        ? makeEmptyAwareTooltip({ shared: true })
                        : (id === 'weight' || id === 'waist')
                        ? makeEmptyAwareTooltip({
                            formatValue: (ctx) => fmtDualUnit(ctx.y as number, (data.suffix ?? '') as string, id.toString()),
                            extraRows: yoyExtraRows,
                        })
                        : id === 'gym'
                        ? makeEmptyAwareTooltip({
                            // Multi-series stacked column \u2014 same layout as
                            // transactions (date + Total + sorted-desc per
                            // muscle), but counts not currency.
                            shared: true,
                            sharedValueFormatter: (y) => String(y),
                        })
                        : id === 'snap'
                        ? function (this: any): string | false {
                            // Shared: date header + "Daily gain" and "Score" rows; estimate
                            // points (gap-fills + manually-flagged) get a grey "(est.)" tag.
                            const pts = (this.points || []) as any[];
                            const header = '<span style="font-size:0.8em">'
                                + Highcharts.dateFormat('%b %e, %Y', this.x as number) + '</span><br/>';
                            let body = '';
                            for (const p of pts) {
                                if (p.y == null) continue;
                                const isEst = p.custom?.estimate === 1;
                                const estLabel = isEst
                                    ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                                    : '';
                                const dot = typeof p.series.color === 'string' ? p.series.color : '#aaa';
                                body += `<span style="color:${dot}">●</span> ${p.series.name}: `
                                    + `<b>${(p.y as number).toLocaleString('en-US')}${estLabel}</b><br/>`;
                            }
                            return body ? header + body : false;
                        }
                        : undefined  // trades / trades_gold \u2014 out of scope per CONTEXT.md
                },
                plotOptions: {
                    spline: {
                        lineWidth: 4,
                        states: {
                            hover: {
                                lineWidth: 5
                            }
                        },
                        marker: {
                            enabled: false
                        },
                        pointInterval: 86400000, // one day
                        pointStart: Date.parse(dates_array[0]),
                    },
                    column: {
                        stacking: (id === 'alc' && (data_array as any).yoy_enabled === true) ? undefined : 'normal',
                        borderWidth: 0,
                        states: {
                            hover: {
                                borderWidth: 1,
                            }
                        },
                        pointInterval: 86400000, // one day
                        pointStart: Date.parse(dates_array[0]),
                    },
                    series: {
                        cursor: ['alc', 'weight', 'waist', 'gym', 'km', 'stairmaster', 'snap'].includes(id.toString()) ? 'pointer' : undefined,
                        point: {
                            events: {
                                click: function () {
                                    // Set guard flag so chart.events.click skips on this tick
                                    _pointClickInProgress = true;
                                    let date = Highcharts.dateFormat('%Y-%m-%d', this.x);
                                    let value = this.y as number;
                                    updateValue(id.toString(), date, value, this.series.index);
                                    // Reset after current event loop tick
                                    setTimeout(() => { _pointClickInProgress = false; }, 0);
                                }
                            }
                        }
                    }
                },
                credits: {
                    enabled: false
                }
            } as any));
        }
    }

    function updateValue(chart : string, date : string, value : number | undefined = undefined, seriesIndex : number = 0) {
        if (!['alc', 'weight', 'waist', 'gym', 'km', 'stairmaster', 'snap'].includes(chart)) return;
        if (chart === 'km') {
            openRunsModal(date);
            return;
        }
        if (chart === 'stairmaster') {
            openStairmasterModal(date);
            return;
        }
        if (chart === 'gym') {
            openGymModal(date);
            return;
        }
        openModal(chart, date);
    }

    function generateWeekendPlotBands(dates: string[]): Highcharts.XAxisPlotBandsOptions[] {
        if (!dates || dates.length === 0) {
            return [];
        }

        const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];
        let currentDate = new Date(dates[0].substring(0, 10) + 'T00:00:00');
        const endDate = new Date(dates[dates.length - 1].substring(0, 10) + 'T00:00:00');

        // Advance to first Saturday (day 6)
        while (currentDate.getDay() !== 6) {
            currentDate.setDate(currentDate.getDate() + 1);
            if (currentDate > endDate) {
                return plotBands;
            }
        }

        while (currentDate <= endDate) {
            let weekendStart = new Date(currentDate);
            weekendStart.setHours(-8, 0, 0, 0);
            let weekendEnd = new Date(currentDate);
            weekendEnd.setDate(weekendEnd.getDate() + 1);
            weekendEnd.setHours(8, 0, 0, 0);

            plotBands.push({
                from: weekendStart.getTime(),
                to: weekendEnd.getTime(),
                color: 'rgba(128, 128, 128, 0.1)',
                label: {
                    text: '',
                    style: {
                        color: '#999'
                    }
                }
            });

            currentDate.setDate(currentDate.getDate() + 7);
        }

        return plotBands;
    }

    function diffForHumans(milliseconds : number, parts = 2) {
        let totalSeconds = Math.floor(milliseconds / 1000);
        let components = [];

        let days = Math.floor(totalSeconds / 86400);
        let hours = Math.floor((totalSeconds % 86400) / 3600);
        let minutes = Math.floor((totalSeconds % 3600) / 60);
        let seconds = totalSeconds % 60;

        if (days > 0) components.push(`${days}d`);
        if (hours > 0) components.push(`${hours}h`);
        if (minutes > 0) components.push(`${minutes}m`);
        if (seconds > 0) components.push(`${seconds}s`);

        if (components.length > parts) {
            components = components.slice(0, parts);
        }

        return components.join(' ');
    }
}

function tables() {
    let table = document.querySelector('.datatable[data-table="tables"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="tables"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getTables',
            data: function(data) {
                return data;
            }
        },
        columns: [
            { data: 'table', className: "align-middle" },
            { data: null, className: "align-middle", render: (data) => color(data.size,data.size_color,true,2,false)},
        ],
        order: [[1,"desc"]] as Order[]
    });

    if(document.querySelector('.datatable[data-table="table_groups"]')) {
        new DataTable('.datatable[data-table="table_groups"]',{
            paging: false,
            searching: false,
            info: false,
            order: [[2,"desc"]] as Order[],
        });
    }
}

function source() {
    let chart = document.getElementById('chart_source') as HTMLElement;
    if(!chart) {
        return;
    }

    let series = [] as Highcharts.SeriesOptionsType[];
    let series_data = [] as any;
    Object.values(source_data).forEach((data : any) => {
        series_data.push({
            name: data.name,
            y: data.lines
        });
    });
    series.push({
        name: 'Lines',
        colorByPoint: true,
        data: series_data
    } as Highcharts.SeriesOptionsType);

    Highcharts.chart('chart_source', Highcharts.merge(getChartTheme(), {
        series: series,
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 600,
        },
        title: {
            text: 'Source',
            style: {
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false
        }
    } as any));
}

function source_language() {
    let chart = document.getElementById('chart_source_language') as HTMLElement;
    if(!chart) {
        return;
    }

    let series = [] as Highcharts.SeriesOptionsType[];
    let series_data = [] as any;
    Object.values(source_language_data).forEach((data : any) => {
        series_data.push({
            name: data.name,
            y: data.lines
        });
    });
    series.push({
        name: 'Lines',
        colorByPoint: true,
        data: series_data
    } as Highcharts.SeriesOptionsType);

    Highcharts.chart('chart_source_language', Highcharts.merge(getChartTheme(), {
        series: series,
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 600,
        },
        title: {
            text: 'Language',
            style: {
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false
        }
    } as any));
}

function months() {
    if(typeof data_array !== 'undefined') {
        on('click','.js-month',(target : HTMLElement) => {
            let month = target.getAttribute('name');
            if(!month) {
                return;
            }
            target.classList.add('active');
            let year = new Date().getFullYear().toString();
            let from_date = year + '-' + month + '-01';
            let lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
            let to_date = year + '-' + month + '-' + lastDay.toString().padStart(2, '0');
            let fromInput = document.querySelector('input[name="from"]') as HTMLInputElement;
            let toInput = document.querySelector('input[name="to"]') as HTMLInputElement;
            if(fromInput) fromInput.value = from_date;
            if(toInput) toInput.value = to_date;
            setTimeout(() => {
                location.href = '?from=' + from_date + '&to=' + to_date;
            }, 500);
        });
    }
}

function heatmaps() {
    if(typeof heatmap_data === 'undefined') return;

    const year = heatmap_data.year;
    const metrics = ['alcohol', 'runs', 'gym', 'reading', 'weight'] as const;

    for(const metric of metrics) {
        const container = document.getElementById('heatmap_' + metric);
        if(!container) continue;

        const colorLight = container.dataset.colorLight || '#e0e0e0';
        const colorDark = container.dataset.colorDark || '#333333';
        const data = heatmap_data[metric] || {};

        // Find max value for scaling
        const values = Object.values(data) as number[];
        const maxVal = values.length > 0 ? Math.max(...values) : 1;

        // Build grid: 53 weeks x 7 days, fill container width
        const grid = document.createElement('div');
        grid.style.display = 'grid';
        grid.style.gridTemplateColumns = 'repeat(53, 1fr)';
        grid.style.gridTemplateRows = 'repeat(7, 1fr)';
        grid.style.gap = '2px';
        grid.style.width = '100%';
        grid.style.aspectRatio = '53 / 7';

        const startDate = new Date(year, 0, 1);
        const startDow = (startDate.getDay() + 6) % 7; // Monday = 0

        for(let week = 0; week < 53; week++) {
            for(let dow = 0; dow < 7; dow++) {
                const dayOffset = week * 7 + dow - startDow;
                const date = new Date(year, 0, 1 + dayOffset);
                const cell = document.createElement('div');
                cell.style.borderRadius = '2px';
                cell.style.gridColumn = (week + 1).toString();
                cell.style.gridRow = (dow + 1).toString();

                if(date.getFullYear() !== year) {
                    cell.style.backgroundColor = 'transparent';
                } else {
                    const dateStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
                    const val = data[dateStr] || 0;

                    if(val === 0) {
                        // CSS custom property resolves live at paint time per theme (Pitfall 7).
                        cell.style.backgroundColor = 'var(--ksat-surface-raised)';
                    } else {
                        const intensity = Math.min(val / maxVal, 1);
                        cell.style.backgroundColor = interpolateColor(colorLight, colorDark, intensity);
                    }

                    cell.dataset.bsToggle = 'tooltip';
                    cell.dataset.bsTitle = dateStr + ': ' + (metric === 'weight' ? (val ? 'Logged' : 'No data') :
                                 metric === 'reading' ? formatSeconds(val) : val);
                }

                grid.appendChild(cell);
            }
        }

        container.appendChild(grid);
    }

    init_tooltips();
}

function interpolateColor(light: string, dark: string, t: number): string {
    const parse = (hex: string) => {
        hex = hex.replace('#', '');
        return [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
    };
    const [r1,g1,b1] = parse(light);
    const [r2,g2,b2] = parse(dark);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r},${g},${b})`;
}

function formatSeconds(secs: number): string {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if(h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
}

